/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines */
import PropTypes from "prop-types";
import React, { useRef } from "react";
import ButtonComponent from "../../../atoms/button";
import Drawer from "../../../molecules/drawer";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import TextField from "../../../atoms/textField";
import Typography from "../../../atoms/typography";
import { Tabs } from "../../../molecules/tabion";
import Tab from "../../../molecules/tabion/tab";
import TabList from "../../../molecules/tabion/tabList";
import TabPanel from "../../../molecules/tabion/tabPanel";
import { plpCleverTapEvents, plpPageConstants } from "../../../../constants/pageConstants/plpConstants";
import CheckboxItemList from "../../../molecules/checkboxItemList";
import { useCallback, useEffect, useState, useMemo } from "react";
import {
  requestToFetchProductFilterInMobile,
  requestToFetchProductListingAction,
  setClickedFilterChip,
} from "../../../../store/slices/plpSlice";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { unlockBody } from "../../../../utils/document";
import { COUNTRIES, fnpPageType, INDIAN_RUPEE, CURRENCY_ID } from "../../../../constants/common/common";
import { removeEmptyNullAndUndefinedFromObject, updateCurrency } from "../../../../utils/common";
import {
  getReplaceFiltersUrlForMobile,
  currencyConverter as currencyConverterUtil,
  reverseCurrencyConverter as reverseCurrencyConverterUtil,
  snapToStepSize,
  parseAppliedFilters,
  buildPriceFilterString,
  buildFilterPayload,
  getFilterOptionLabel,
  sanitizeNumericInput,
  calculatePriceRangeFromInput,
  getFilterTabCount,
  getFacetTotalCount,
  isSameRange,
  areObjectsEqual,
  priceToSliderValue,
  sliderValueToPrice,
  trackPriceFilterSelected,
  trackFilterApplied,
} from "../../../../utils/plp/filters";
import { convertFilterObjToArray, toTitleCase } from "../../../../utils/common";
import useAnalytics from "../../../../analytics/useAnalytics";
import { GA4_EVENTS } from "../../../../analytics/gaConstant";
import isEmpty from "lodash.isempty";
import { usePathname } from "next/navigation";
import CookieUtil from "../../../../utils/storage/cookieUtil";
import PriceHistogram from "../../../molecules/priceHistogram";
import { cleverTapCommonWords } from "../../../../analytics/clevertapConstants";

/**
 * FilterMobile component for filtering items on mobile devices.
 * @param {object} props - Component props.
 * @param {boolean} props.isOpen - Flag indicating if the filter is open.
 * @param {function} props.onClose - Function to handle closing the filter.
 * @param {Array} props.data - Array of filter data objects.
 * @param {number} props.totalProductCount - Total count of available products.
 * @returns {JSX.Element} FilterMobile component JSX.
 */
export default function FilterMobile({ isOpen, onClose, data, categoryId, setFilterCount, filterTotalCount }) {
  const pathName = usePathname();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [priceRange, setPriceRange] = useState(null);
  const [localPriceRange, setLocalPriceRange] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);
  const [isProcessingInput, setIsProcessingInput] = useState(false);
  const [enterPressed, setEnterPressed] = useState(false);
  // Store initial state when drawer opens to reset on close
  const [initialSelectedOptions, setInitialSelectedOptions] = useState({});
  const [initialPriceRange, setInitialPriceRange] = useState(null);
  const [initialMinPrice, setInitialMinPrice] = useState("");
  const [initialMaxPrice, setInitialMaxPrice] = useState("");
  const [initialLocalPriceRange, setInitialLocalPriceRange] = useState([]);
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const pathname = usePathname();
  const tabRef = useRef({});

  const priceFacet = data?.find((facet) => facet.facetFieldName === "defaultPrice");
  const urlParams = useMemo(() => new URLSearchParams(typeof window === "object" ? window?.location?.search : ""), []);
  const { appliedSort, appliedFilters, totalProducts, pincode, intlPincode, selectedCurrency, clickedFilterChip } =
    useSelector(
      (state) => ({
        appliedSort: state?.plp?.appliedSort,
        appliedFilters: state?.plp?.appliedFilters,
        totalProducts: state?.plp?.details?.total,
        pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
        intlPincode: state?.locationLock?.selectedIntLocation?.pincode,
        selectedCurrency: state?.currencyList?.userSelectedCurrency,
        clickedFilterChip: state?.plp?.clickedFilterChip,
      }),
      shallowEqual,
    );

  const selectedFilterChipIndex = data?.findIndex((item) => item?.facetFieldName === clickedFilterChip);

  useEffect(() => {
    if (tabRef?.current) {
      tabRef?.current?.[clickedFilterChip]?.click();
    }
  }, [selectedFilterChipIndex]);

  useEffect(() => {
    if (clickedFilterChip?.length > 0) {
      trackEvent(
        plpCleverTapEvents?.FILTER_VIEWED,
        {
          page_name: pathName,
          page_type: cleverTapCommonWords?.PLP,
          filter_attribute: toTitleCase(clickedFilterChip),
        },
        ["clevertap"],
      );
    }
  }, [clickedFilterChip]);

  /** */
  const currencyConverter = (value) => currencyConverterUtil(selectedCurrency, value, updateCurrency);

  /**
   * Convert from selected currency back to base currency (INR/API currency)
   * User inputs are in selected currency, but API needs base currency values
   */
  const reverseCurrencyConverter = (convertedValue) =>
    reverseCurrencyConverterUtil(selectedCurrency, convertedValue, CURRENCY_ID);

  const catalogId = useSelector((state) => state.appConfig.catalogId);

  // Initialize filter count on mount and when appliedFilters change (for page refresh)
  useEffect(() => {
    if (appliedFilters?.length > 0 && appliedFilters?.[0] !== "") {
      const filterCount = appliedFilters.length;
      setFilterCount?.(filterCount);
    } else {
      setFilterCount?.(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  // Initialize filter state from applied filters when drawer opens
  useEffect(() => {
    if (isOpen && initialLoad) {
      if (appliedFilters?.length > 0 && appliedFilters?.[0] !== "") {
        const filterCount = appliedFilters.length;
        setFilterCount?.(filterCount);

        let selectedOptionsFromFilters = {};
        let priceRangeFromFilters = null;

        try {
          const parsed = parseAppliedFilters(appliedFilters);
          selectedOptionsFromFilters = parsed?.selectedOptionsFromFilters || {};
          priceRangeFromFilters = parsed?.priceRangeFromFilters || null;
        } catch (error) {
          console.error("Error parsing applied filters:", error);
          selectedOptionsFromFilters = {};
          priceRangeFromFilters = null;
        }

        // Set current state
        setSelectedOptions(selectedOptionsFromFilters);
        // Store initial state for reset on close
        setInitialSelectedOptions(selectedOptionsFromFilters);

        if (priceRangeFromFilters && Array.isArray(priceRangeFromFilters) && priceRangeFromFilters.length >= 2) {
          setPriceRange(priceRangeFromFilters);
          setLocalPriceRange(priceRangeFromFilters);
          const convertedMin = currencyConverter(priceRangeFromFilters[0]);
          const convertedMax = currencyConverter(priceRangeFromFilters[1]);
          setMinPrice(convertedMin);
          setMaxPrice(convertedMax);
          // Store initial state for reset on close
          setInitialPriceRange(priceRangeFromFilters);
          setInitialMinPrice(convertedMin);
          setInitialMaxPrice(convertedMax);
          setInitialLocalPriceRange(priceRangeFromFilters);
        } else {
          // No price filter, store empty initial state
          setInitialPriceRange(null);
          setInitialMinPrice("");
          setInitialMaxPrice("");
          setInitialLocalPriceRange([]);
        }
      } else {
        // No filters applied, reset everything
        setSelectedOptions({});
        setInitialSelectedOptions({});
        setPriceRange(null);
        setInitialPriceRange(null);
        setMinPrice("");
        setMaxPrice("");
        setInitialMinPrice("");
        setInitialMaxPrice("");
        setFilterCount?.(0);
        setInitialLocalPriceRange([]);
      }
      setInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appliedFilters]);

  /**
   * Dispatch the selected filters when option change listner triggered.
   */
  const onSelectedOptions = useCallback(
    (option, sortOption, selectedPincode, priceRangeFilter = null, applyAll = false, isScrollTop = false) => {
      if (typeof window !== "undefined" && isScrollTop) {
        window.scrollTo(0, 0);
      }

      let appliedFilterArray = [];
      try {
        appliedFilterArray = convertFilterObjToArray(option) || [];
      } catch (error) {
        console.error("Error converting filter options to array:", error);
        appliedFilterArray = [];
      }

      // Add price range filter if provided
      const priceFilterString = buildPriceFilterString(priceFacet?.facetFieldName, priceRangeFilter);
      if (priceFilterString) {
        appliedFilterArray.push(priceFilterString);
      }

      // Get email from faul cookie
      const email = CookieUtil.getCookie("faul") || "";
      const fnpId = CookieUtil.getCookie("ab_user_id");

      // Check if this is a search page
      const isSearchPage = categoryId === plpPageConstants.SEARCH;
      const searchQuery = isSearchPage ? urlParams.get("qs") : null;

      setFilterCount(appliedFilterArray?.length || 0);

      let newUrl = "";
      try {
        newUrl = getReplaceFiltersUrlForMobile(urlParams, appliedFilterArray, sortOption) || "";
      } catch (error) {
        console.error("Error building filter URL:", error);
        newUrl = window?.location?.href || "";
      }

      let payload = {};
      try {
        payload = buildFilterPayload({
          appliedFilterArray,
          sortOption,
          selectedPincode,
          intlPincode,
          catalogId,
          categoryId,
          isSearchPage,
          searchQuery,
          pageSize: plpPageConstants.MOBILE_PRODUCT_PER_PAGE,
          initialPage: plpPageConstants.INITIAL_PAGE,
          searchPageType: plpPageConstants.SEARCH,
          defaultCountry: COUNTRIES?.INDIA,
          email,
          fnpId,
        });
      } catch (error) {
        console.error("Error building filter payload:", error);
        payload = {};
      }

      if (applyAll) {
        dispatch(requestToFetchProductListingAction(removeEmptyNullAndUndefinedFromObject(payload)));
      } else {
        dispatch(requestToFetchProductFilterInMobile(removeEmptyNullAndUndefinedFromObject(payload)));
      }

      // Safe window.history access for SSR
      if (typeof window !== "undefined" && window.history && newUrl) {
        try {
          window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, "", newUrl);
        } catch (error) {
          console.error("Error updating browser history:", error);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pincode, intlPincode, urlParams, catalogId, categoryId],
  );
  // Initialize localPriceRange from API response (minPrice, maxPrice)
  useEffect(() => {
    if (priceFacet && priceFacet.facetOptions?.[0]) {
      const minPrice = priceFacet.facetOptions[0].roundedMinPrice || 0;
      const maxPrice = priceFacet.facetOptions[0].roundedMaxPrice || 100000;

      // If priceRange prop is provided (from URL), adjust it to fit within new min/max bounds
      if (priceRange && priceRange.length > 0) {
        const [selectedMin, selectedMax] = priceRange;

        setLocalPriceRange(priceRange);
        setMinPrice(currencyConverter(selectedMin));
        setMaxPrice(currencyConverter(selectedMax));
      } else {
        setLocalPriceRange([minPrice, maxPrice]);
        setMinPrice(currencyConverter(minPrice));
        setMaxPrice(currencyConverter(maxPrice));
      }
    }
  }, [priceFacet, priceRange, selectedOptions, appliedSort, pincode, selectedCurrency?.currency?.currencyCode]);

  useEffect(() => {
    if (!initialLoad) {
      onSelectedOptions(selectedOptions, appliedSort, pincode, priceRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  /**
   * Snap value to appropriate step size based on outlier threshold
   * @param {number} value - The value to snap
   * @returns {number} Snapped value
   */
  const snapToStepSizeLocal = (value) => snapToStepSize(value, priceFacet?.facetOptions?.[0]);

  /**
   * Handle price range changes from slider (slider values are 0-100, need to convert to actual prices)
   */
  const handlePriceChange = (newRange) => {
    if (!priceFacet?.facetOptions?.[0]) return;

    // Validate newRange is a valid array
    if (!Array.isArray(newRange) || newRange.length < 2) {
      console.warn("Invalid price range:", newRange);
      return;
    }

    const [sliderMin, sliderMax] = newRange;
    const priceOptions = priceFacet.facetOptions[0];

    // Convert slider positions (0-100) to actual price values
    const min = sliderValueToPrice(sliderMin, priceOptions);
    const max = sliderValueToPrice(sliderMax, priceOptions);

    const roundedMaxPrice = priceOptions.roundedMaxPrice;
    const roundedMinPrice = priceOptions.roundedMinPrice;

    // Clamp values to valid bounds first
    const clampedMin = Math.max(roundedMinPrice, Math.min(min, roundedMaxPrice));
    const clampedMax = Math.max(roundedMinPrice, Math.min(max, roundedMaxPrice));

    // Snap values to appropriate step sizes
    const snappedMin = snapToStepSizeLocal(clampedMin);
    const snappedMax = snapToStepSizeLocal(clampedMax);

    // Ensure min doesn't exceed max and both are within bounds (in base currency)
    const finalMin = Math.min(snappedMin, snappedMax, roundedMaxPrice);
    const finalMax = Math.max(snappedMin, snappedMax);
    const snappedRange = [finalMin, finalMax];

    setMinPrice(currencyConverter(finalMin));
    setMaxPrice(currencyConverter(finalMax));
    setLocalPriceRange(snappedRange);
    setPriceRange(snappedRange);
  };

  /**
   * Handle price range changes - only update local state, don't apply immediately
   */
  const onPriceChange = useCallback(
    (newPriceRange) => {
      handlePriceChange(newPriceRange);
    },
    [priceFacet],
  );

  /**
   * Handle price range final selection - only update local state, no API call
   */
  const onPriceChangeComplete = useCallback(
    (newPriceRange) => {
      try {
        setInitialLoad(false);

        // Track filter selected event for price
        trackPriceFilterSelected(newPriceRange, trackEvent, plpCleverTapEvents, pathname, fnpPageType?.CATEGORY);

        // Only update local state, don't trigger API call - filters will be applied when "Apply Filters" is clicked
        // newPriceRange is in base currency (INR), convert to selected currency for display
        if (newPriceRange && Array.isArray(newPriceRange)) {
          setPriceRange(newPriceRange);
          if (newPriceRange[0] != null) {
            setMinPrice(currencyConverter(newPriceRange[0]));
          }
          if (newPriceRange[1] != null) {
            setMaxPrice(currencyConverter(newPriceRange[1]));
          }
        }

        if (onSelectedOptions && typeof onSelectedOptions === "function") {
          try {
            onSelectedOptions(selectedOptions || {}, appliedSort, pincode, newPriceRange);
          } catch (error) {
            console.error("Error in onSelectedOptions:", error);
          }
        }
      } catch (error) {
        console.error("Error in onPriceChangeComplete:", error);
      }
    },
    [selectedOptions, appliedSort, pincode, onSelectedOptions, pathname, trackEvent, currencyConverter],
  );

  /**
   * Handle slider change complete - convert slider range to price range and update state
   * @param {Array<number>} sliderRange - The slider range array [min, max] (0-100)
   * @param {Object} priceOptions - The price options from facet
   */
  const handleSliderChangeComplete = useCallback(
    (sliderRange, priceOptions) => {
      // Validate sliderRange
      if (!Array.isArray(sliderRange) || sliderRange.length < 2) {
        console.warn("Invalid slider range:", sliderRange);
        return;
      }

      if (!priceOptions) {
        console.warn("Price options not available");
        return;
      }

      // Convert slider positions to actual prices
      const [sliderMin, sliderMax] = sliderRange;
      const min = sliderValueToPrice(sliderMin, priceOptions);
      const max = sliderValueToPrice(sliderMax, priceOptions);

      const roundedMaxPrice = priceOptions?.roundedMaxPrice;
      const roundedMinPrice = priceOptions?.roundedMinPrice;

      // Clamp values to valid bounds
      const clampedMin = Math.max(roundedMinPrice, Math.min(min, roundedMaxPrice));
      const clampedMax = Math.max(roundedMinPrice, Math.min(max, roundedMaxPrice));

      // Snap values to appropriate step sizes
      const snappedMin = snapToStepSizeLocal(clampedMin);
      const snappedMax = snapToStepSizeLocal(clampedMax);

      // Ensure min doesn't exceed max
      const finalMin = Math.min(snappedMin, snappedMax, roundedMaxPrice);
      const finalMax = Math.max(snappedMin, snappedMax);
      const finalRange = [finalMin, finalMax];

      // Update local state
      setLocalPriceRange(finalRange);
      setMinPrice(currencyConverter(finalMin));
      setMaxPrice(currencyConverter(finalMax));

      // Update local state only, don't trigger API call - filters will be applied when "Apply Filters" is clicked
      onPriceChangeComplete && onPriceChangeComplete(finalRange);
    },
    [currencyConverter, snapToStepSizeLocal, onPriceChangeComplete],
  );

  /**
   * Handle input field changes
   */
  const onHandleFieldChange = (e, isMin) => {
    const { value } = e.target;
    const sanitizedValue = sanitizeNumericInput(value);

    if (isMin) {
      setMinPrice(sanitizedValue);
    } else {
      setMaxPrice(sanitizedValue);
    }
  };

  /** */
  const returnInputValue = (isMin) => {
    if (isMin) {
      return minPrice || 0;
    } else {
      return maxPrice || 0;
    }
  };

  /**
   * Handle input validation - only update local state, no API call
   */
  const onClickGo = () => {
    if (isProcessingInput) return;
    setIsProcessingInput(true);

    let finalMinBase = 0;
    let finalMaxBase = 100000;

    try {
      const result = calculatePriceRangeFromInput(
        minPrice,
        maxPrice,
        priceFacet?.facetOptions?.[0],
        reverseCurrencyConverter,
      );
      finalMinBase = result?.finalMinBase ?? 0;
      finalMaxBase = result?.finalMaxBase ?? 100000;
    } catch (error) {
      console.error("Error calculating price range from input:", error);
      // Use defaults or current priceRange if available
      if (priceRange && Array.isArray(priceRange) && priceRange.length >= 2) {
        finalMinBase = priceRange[0];
        finalMaxBase = priceRange[1];
      }
    }

    // Store in base currency (INR) for API - this is what gets sent to API
    setLocalPriceRange([finalMinBase, finalMaxBase]);
    setPriceRange([finalMinBase, finalMaxBase]);

    // Display converted values in UI (selected currency)
    setMinPrice(currencyConverter(finalMinBase));
    setMaxPrice(currencyConverter(finalMaxBase));

    // Trigger visual update (pass base currency values)
    onPriceChange && onPriceChange([finalMinBase, finalMaxBase]);

    // Only trigger API call if the range really changed (using base currency values)
    if (priceRange && Array.isArray(priceRange) && priceRange.length >= 2) {
      if (!isSameRange([finalMinBase, finalMaxBase], priceRange)) {
        onPriceChangeComplete && onPriceChangeComplete([finalMinBase, finalMaxBase]);
      }
    } else {
      // If priceRange is null/undefined, always trigger
      onPriceChangeComplete && onPriceChangeComplete([finalMinBase, finalMaxBase]);
    }

    // Reset flag after a short delay
    setTimeout(() => {
      setIsProcessingInput(false);
    }, 100);
  };

  /**
   * Handle blur event
   */
  const onBlur = () => {
    // Skip if Enter was just pressed to avoid duplicate calls
    if (enterPressed) return;
    onClickGo();
  };

  /**
   * Handle focus event - select all text
   */
  const onFocus = (e) => {
    // Select all text when user focuses on the input
    if (e?.target && typeof e.target.select === "function") {
      try {
        e.target.select();
      } catch (error) {
        console.error("Error selecting input text:", error);
      }
    }
  };

  /**
   * Handle key down event
   */
  const onKeyDown = (e) => {
    // Trigger validation when user presses Enter
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent default behavior
      setEnterPressed(true); // Mark that Enter was pressed
      onClickGo();
      // Reset the flag after processing
      setTimeout(() => {
        setEnterPressed(false);
      }, 100);
    }
  };

  /**
   *
   * @returns
   */
  const getLabel = (option) => getFilterOptionLabel(option);

  /**
   * Handle filter option changes with dynamic value handling
   * @param {string} value - The option value
   * @param {string} facetName - The facet field name
   * @param {string} displayName - The display name for the option
   */
  const onOptionChange = (displayName, facetName) => {
    setInitialLoad(false);

    // Special handling for price filter - use min-max format
    let filterValue;
    if (facetName === "defaultPrice") {
      // For price filter, we don't use this function - it's handled by onPriceChange
      return;
    } else {
      // For all other facets, use displayName
      filterValue = displayName;
    }

    // Use displayName as the key to ensure uniqueness
    const optionKey = displayName;

    if (selectedOptions?.[optionKey]) {
      // Remove filter if already selected
      const option = { ...selectedOptions };
      delete option[optionKey];
      setSelectedOptions({ ...option });
    } else {
      // Add filter
      setSelectedOptions((prev) => ({
        ...prev,
        [optionKey]: `${facetName}:${filterValue}`,
      }));

      // Track events with correct values
      trackEvent(
        plpCleverTapEvents.FILTER_SELECTED,
        {
          page_name: pathname,
          page_type: fnpPageType.CATEGORY,
          filter: `${facetName} : ${filterValue}`,
        },
        ["clevertap"],
      );
      trackEvent(
        GA4_EVENTS.FILTER_CLICKED,
        {
          active_filters: [...(appliedFilters || []), `${facetName} : ${filterValue}`],
          selected_filter: filterValue,
        },
        ["ga4"],
      );
    }
  };

  /**
   * Reset filter state to initial values when drawer is closed without applying
   */
  const resetToInitialState = () => {
    setSelectedOptions(initialSelectedOptions);
    setPriceRange(initialPriceRange);
    setMinPrice(initialMinPrice);
    setMaxPrice(initialMaxPrice);
    // Reset filter count to actual applied filters count (from Redux)
    const actualFilterCount = appliedFilters?.length > 0 && appliedFilters?.[0] !== "" ? appliedFilters.length : 0;
    setFilterCount?.(actualFilterCount);
    if (initialLocalPriceRange.length > 0) {
      setLocalPriceRange(initialLocalPriceRange);
    } else if (priceFacet?.facetOptions?.[0]) {
      const minPrice = priceFacet.facetOptions[0].roundedMinPrice || 0;
      const maxPrice = priceFacet.facetOptions[0].roundedMaxPrice || 100000;
      setLocalPriceRange([minPrice, maxPrice]);
    }
    setInitialLoad(true);
  };

  /**
   * Handle drawer close - reset to initial state if not applying filters
   */
  const onDrawerCloseHandler = () => {
    resetToInitialState();
    if (onClose && typeof onClose === "function") {
      onClose();
    }
  };

  /**
   * This will close the filter drawer and apply all selected filters including price range.
   */
  const onApplyFilters = () => {
    try {
      setInitialLoad(false);

      // Update initial state to current state since filters are being applied
      setInitialSelectedOptions(selectedOptions);
      setInitialPriceRange(priceRange);
      setInitialMinPrice(minPrice);
      setInitialMaxPrice(maxPrice);
      setInitialLocalPriceRange(localPriceRange);

      // Track Filter Applied event
      trackFilterApplied(selectedOptions, priceRange, trackEvent, plpCleverTapEvents, pathname, fnpPageType?.CATEGORY);

      // Apply filters
      if (onSelectedOptions && typeof onSelectedOptions === "function") {
        try {
          onSelectedOptions(selectedOptions || {}, appliedSort, pincode, priceRange, true, true);
        } catch (error) {
          console.error("Error applying filters:", error);
        }
      }

      // Unlock body and close drawer
      if (typeof unlockBody === "function") {
        try {
          unlockBody();
        } catch (error) {
          console.error("Error unlocking body:", error);
        }
      }

      if (onClose && typeof onClose === "function") {
        try {
          onClose();
        } catch (error) {
          console.error("Error closing filter drawer:", error);
        }
      }
    } catch (error) {
      console.error("Error in onApplyFilters:", error);
      // Still try to close the drawer even if tracking fails
      try {
        if (typeof unlockBody === "function") unlockBody();
        if (onClose && typeof onClose === "function") onClose();
      } catch (closeError) {
        console.error("Error closing filter drawer:", closeError);
      }
    }
  };

  /**
   * This will clear all the applied filters.
   */
  const onClearFilters = () => {
    if (isEmpty(selectedOptions) && !priceRange) {
      return;
    }
    setInitialLoad(false);
    setSelectedOptions({});
    setPriceRange(null);
    setFilterCount?.(0);
    setMaxPrice("");
    setMinPrice("");

    // Apply the cleared filters immediately
    onSelectedOptions({}, appliedSort, pincode, null, true, true);

    setTimeout(() => {
      unlockBody();
      onClose();
    }, 100);
  };

  /**
   * Calculate current filter count based on selectedOptions and priceRange
   * If current state matches initial state (no changes), show count 0
   * If there are changes, show the complete count of all filters
   * Both selectedOptions and priceRange are compared with their initial states
   */
  const currentFilterCount = useMemo(() => {
    try {
      // Check if current state matches initial state (no changes)
      const selectedOptionsChanged = !areObjectsEqual(selectedOptions, initialSelectedOptions);

      // Handle price range comparison - both null/undefined means no change
      let priceRangeChanged = false;
      if (!priceRange && !initialPriceRange) {
        // Both are null/undefined, no change
        priceRangeChanged = false;
      } else if (!priceRange || !initialPriceRange) {
        // One is null and other is not, there's a change
        priceRangeChanged = true;
      } else {
        // Both are arrays, compare them
        priceRangeChanged = !isSameRange(priceRange, initialPriceRange);
      }

      // If no changes, return 0
      if (!selectedOptionsChanged && !priceRangeChanged) {
        return 0;
      }

      // If there are changes, calculate total count
      let count = 0;

      // Count selected options from local state
      if (selectedOptions && typeof selectedOptions === "object") {
        const filterArray = convertFilterObjToArray(selectedOptions) || [];
        count += filterArray.length;
      }

      // Add 1 if price range is selected in local state
      if (priceRange && Array.isArray(priceRange) && priceRange.length >= 2) {
        count += 1;
      }

      return count;
    } catch (error) {
      console.error("Error calculating filter count:", error);
      return 0;
    }
  }, [selectedOptions, priceRange, initialSelectedOptions, initialPriceRange]);

  /**
   * Handle price histogram bar click event.
   * Converts the clicked range from selected currency to base currency (INR),
   * validates and clamps the values, snaps them to appropriate step sizes,
   * and updates the local price range state without triggering an API call.
   * @param {Array<number>} convertedRange - The price range in selected currency [min, max]
   * @param {Object} priceOptions - The price options object from facet containing roundedMinPrice, roundedMaxPrice, etc.
   * @returns {void}
   */
  const handleHistogramBarClick = (convertedRange, priceOptions) => {
    // Validate convertedRange
    if (!Array.isArray(convertedRange) || convertedRange.length < 2) {
      console.warn("Invalid converted range from histogram:", convertedRange);
      return;
    }

    // convertedRange is in selected currency from PriceHistogram
    // Convert back to base currency (INR) for API
    let baseMin = 0;
    let baseMax = 100000;

    try {
      baseMin = reverseCurrencyConverter(convertedRange[0]) || 0;
      baseMax = reverseCurrencyConverter(convertedRange[1]) || 100000;
    } catch (error) {
      console.error("Error converting currency in histogram click:", error);
      return;
    }

    const roundedMaxPrice = priceOptions?.roundedMaxPrice;
    const roundedMinPrice = priceOptions?.roundedMinPrice;

    // Clamp values to valid bounds
    const clampedMin = Math.max(roundedMinPrice, Math.min(baseMin, roundedMaxPrice));
    const clampedMax = Math.max(roundedMinPrice, Math.min(baseMax, roundedMaxPrice));

    // Snap values to appropriate step sizes
    const snappedMin = snapToStepSizeLocal(clampedMin);
    const snappedMax = snapToStepSizeLocal(clampedMax);

    // Ensure min doesn't exceed max
    const finalMin = Math.min(snappedMin, snappedMax, roundedMaxPrice);
    const finalMax = Math.max(snappedMin, snappedMax);
    const finalRange = [finalMin, finalMax];

    // Update local state only - don't trigger API call
    // Store in base currency (INR) for API
    setLocalPriceRange(finalRange);
    setPriceRange(finalRange);
    // Display converted values in UI (selected currency)
    setMinPrice(currencyConverter(finalMin));
    setMaxPrice(currencyConverter(finalMax));
    onPriceChangeComplete && onPriceChangeComplete(finalRange);
  };

  /**
   * Filtering the tabs which has more than zero filters available.
   */
  const getFilterTabs = () => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data?.map((item, index) => {
      // Handle price filter specially
      if (item.facetFieldName === "defaultPrice") {
        // Show count (1) if price range is selected
        const priceCount = priceRange ? 1 : 0;
        return (
          <Tab key={`Tab-${item.facetFieldName}`} id={index}>
            <span
              className="flex w-full items-center justify-start p-18 text-14 font-400"
              onClick={() => {
                dispatch(setClickedFilterChip(item.facetFieldName));
              }}
              ref={(el) => {
                if (!item?.facetFieldName) return;
                tabRef.current[item.facetFieldName] = el;
              }}
            >
              {toTitleCase(item?.facetFieldDisplayName)}
              {priceCount > 0 && <div className="pl-8 font-400 text-grey-400">{priceCount}</div>}
            </span>
          </Tab>
        );
      }

      const totalCount = getFacetTotalCount(item?.facetOptions);
      const count = getFilterTabCount(item, selectedOptions);

      return totalCount > 0 ? (
        <Tab key={`Tab-${item.facetFieldName}`} id={index}>
          <span
            className="flex w-full items-center justify-start p-18 text-14 font-400"
            onClick={() => {
              dispatch(setClickedFilterChip(item.facetFieldName));
            }}
            ref={(el) => {
              if (!item?.facetFieldName) return;
              tabRef.current[item.facetFieldName] = el;
            }}
          >
            {toTitleCase(item?.facetFieldDisplayName)}
            {count > 0 && <div className="pl-8 font-400 text-grey-400">{count}</div>}
          </span>
        </Tab>
      ) : (
        <React.Fragment key={`Tab-${item.facetFieldName}`} />
      );
    });
  };

  return (
    <Drawer position="bottom" isOpen={isOpen} onDrawerClose={onDrawerCloseHandler} styles="p-0">
      <div className="h-[75vh]">
        <div className="flex gap-8 border-b border-gray-200 px-16 pb-12">
          <span className="text-16 font-600 text-fnp-500">{plpPageConstants.FILTER}</span>
          <span className="text-base text-grey-400">{`(${filterTotalCount || totalProducts} Gifts available)`}</span>
        </div>
        <div className="mb-10 flex h-full flex-row">
          <Tabs selected={selectedFilterChipIndex > -1 ? selectedFilterChipIndex : 0}>
            <TabList
              className="flex w-full gap-0 py-2"
              parentClassName="w-[200px]"
              defaultWidth=""
              isActiveStyle="bg-fnp-100 relative after:absolute after:top-1/2 after:-translate-y-1/2 after:w-4 after:h-4/6 after:bg-fnp-300 after:-left-1 after:rounded-r-3xl"
              tabListStyle="flex-col justify-around mb-[90px]"
              selected={selectedFilterChipIndex}
            >
              {getFilterTabs()}
            </TabList>
            {Array.isArray(data) && data.length > 0
              ? data.map((item) => {
                  if (!item || !item.facetFieldName) {
                    return null;
                  }

                  return (
                    <TabPanel
                      key={`Panel-${item?.facetFieldName}`}
                      className="border-fnp-100 flex h-[60vh] w-full flex-col gap-5 overflow-y-auto border-l-2 px-5 py-16"
                    >
                      {item?.facetFieldName === "defaultPrice" ? (
                        <div className="space-y-12">
                          <span className="text-14 font-600 text-fnp-500">
                            {toTitleCase(item?.facetOptions?.[0]?.displayName)}
                          </span>

                          {/* Histogram */}
                          {item?.facetOptions?.[0]?.priceBuckets?.length > 0 && (
                            <div className="mb-4 w-full">
                              <PriceHistogram
                                priceBuckets={item.facetOptions[0].priceBuckets}
                                effectiveMaxPrice={item.facetOptions[0].effectiveMaxPrice}
                                // eslint-disable-next-line react/jsx-handler-names
                                onBarClick={(convertedRange) =>
                                  handleHistogramBarClick(convertedRange, item.facetOptions?.[0])
                                }
                                currencyConverter={currencyConverter}
                              />
                            </div>
                          )}

                          {/* Slider Labels */}
                          <div className="flex w-full items-center justify-between text-10 text-gray-500">
                            <span>{`${selectedCurrency?.currency?.currencyCode || INDIAN_RUPEE} ${currencyConverter(item.facetOptions?.[0]?.roundedMinPrice)}`}</span>
                            <span>
                              {`${selectedCurrency?.currency?.currencyCode || INDIAN_RUPEE} ${currencyConverter(
                                item.facetOptions?.[0]?.isSkewedDistribution
                                  ? item.facetOptions?.[0]?.effectiveMaxPrice
                                  : item.facetOptions?.[0]?.roundedMaxPrice,
                              )}`}
                              {item.facetOptions?.[0]?.isSkewedDistribution && (
                                <span className="font-semibold text-gray-600">+</span>
                              )}
                            </span>
                          </div>

                          {/* Input fields for minimum and maximum */}
                          <div className="flex w-full items-center justify-between gap-6">
                            <div className="flex flex-1 flex-col gap-1">
                              <div className="flex w-full items-center justify-center p-4">
                                <TextField
                                  type="text"
                                  inputValue={returnInputValue(true)}
                                  className={`w-full border-2 border-thinLightGrey p-4 text-12 font-400 ${selectedCurrency?.currency?.id === "INR" ? "pl-24" : "pl-42"}`}
                                  onChange={(e) => {
                                    onHandleFieldChange(e, true);
                                  }}
                                  onBlur={onBlur}
                                  onFocus={onFocus}
                                  onKeyDown={onKeyDown}
                                  startAdornment={
                                    <Typography variant="p" Tag="p" classes="text-12 font-400">
                                      {selectedCurrency?.currency?.currencyCode || INDIAN_RUPEE}
                                    </Typography>
                                  }
                                />
                              </div>
                            </div>

                            <div className="flex flex-1 flex-col gap-1">
                              <div className="flex w-full items-center justify-center p-4">
                                <TextField
                                  type="text"
                                  inputValue={returnInputValue()}
                                  className={`w-full border-2 border-thinLightGrey p-4 text-12 font-400 ${selectedCurrency?.currency?.id === "INR" ? "pl-24" : "pl-42"}`}
                                  onChange={(e) => {
                                    onHandleFieldChange(e, false);
                                  }}
                                  onBlur={onBlur}
                                  onFocus={onFocus}
                                  onKeyDown={onKeyDown}
                                  startAdornment={
                                    <Typography variant="p" Tag="p" classes="text-12 font-400">
                                      {selectedCurrency?.currency?.currencyCode || INDIAN_RUPEE}
                                    </Typography>
                                  }
                                />
                              </div>
                            </div>
                          </div>

                          {/* Range Slider */}
                          <div className="w-full max-w-full px-8 pt-12">
                            <Slider
                              range={true}
                              min={0}
                              max={100}
                              step={0.1}
                              value={
                                localPriceRange.length > 0
                                  ? [
                                      priceToSliderValue(localPriceRange[0], item.facetOptions?.[0]),
                                      priceToSliderValue(localPriceRange[1], item.facetOptions?.[0]),
                                    ]
                                  : [0, 100]
                              }
                              onChange={onPriceChange}
                              onChangeComplete={(sliderRange) => {
                                handleSliderChangeComplete(sliderRange, item.facetOptions?.[0]);
                              }}
                              marks={
                                item.facetOptions?.[0]?.isSkewedDistribution &&
                                item.facetOptions?.[0]?.effectiveMaxPrice
                                  ? {
                                      70: {
                                        style: {
                                          borderLeft: "1px solid #9CA3AF",
                                          height: "12px",
                                          width: "1px",
                                          marginTop: "-4px",
                                        },
                                        label: "",
                                      },
                                    }
                                  : {}
                              }
                              styles={{
                                track: {
                                  backgroundColor: "#737530",
                                  height: "4px",
                                },
                                handle: {
                                  backgroundColor: "#FFFFFF",
                                  borderColor: "#737530",
                                  borderWidth: "1px",
                                  width: "18px",
                                  height: "18px",
                                  marginTop: "-7px",
                                  boxShadow: "none",
                                  opacity: 1,
                                },
                                rail: {
                                  backgroundColor: "#E5E7EB",
                                  height: "4px",
                                },
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        item?.["facetOptions"]?.map(
                          (option, index) =>
                            option.count > 0 && (
                              <div key={option.displayName} className="text-12">
                                <CheckboxItemList
                                  id={`${option?.displayName?.replace(" ", "")}_${index}`}
                                  label={getLabel(option)}
                                  value={option.displayName}
                                  onChange={() => onOptionChange(option?.displayName, item?.facetFieldName)}
                                  data-parentfilter={item?.facetFieldName}
                                  isChecked={!!selectedOptions?.[option.displayName]}
                                  labelColor="text-grey-400"
                                  activeColorClass="text-[12px]"
                                  labelPlacement="largeGap"
                                />
                              </div>
                            ),
                        )
                      )}
                    </TabPanel>
                  );
                })
              : null}
          </Tabs>
        </div>
        <div className="absolute -bottom-16 flex w-full gap-16 bg-white-900 px-16 py-12">
          <div className="w-1/2">
            <ButtonComponent
              buttonVariant="outlined"
              label="Reset All"
              classes={isEmpty(selectedOptions) && !priceRange ? "opacity-50" : ""}
              size="normal"
              onClick={onClearFilters}
            />
          </div>
          <div className="w-1/2">
            <ButtonComponent
              buttonVariant="filled"
              label={
                currentFilterCount > 0
                  ? `${plpPageConstants.APPLY} ${currentFilterCount} ${currentFilterCount === 1 ? "filter" : "filters"}`
                  : plpPageConstants.APPLY
              }
              size="medium"
              onClick={onApplyFilters}
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
}

FilterMobile.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      facetFieldDisplayName: PropTypes.string.isRequired,
      facetFieldName: PropTypes.string.isRequired,
      facetOptions: PropTypes.arrayOf(
        PropTypes.shape({
          displayName: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
          count: PropTypes.number.isRequired,
          selected: PropTypes.bool.isRequired,
          priceFrom: PropTypes.string,
          priceTo: PropTypes.string,
        }),
      ).isRequired,
    }),
  ).isRequired,
  categoryId: PropTypes.string.isRequired,
  setFilterCount: PropTypes.func.isRequired,
  filterTotalCount: PropTypes.number,
};
